import {assertPartialSchema, createAction, setSessionStorage, useSelector, v} from '../../../lib'
import {AUTH_SET} from '../auth'

export enum SessionActionType {
  SESSION_END_REASON_SET = 'SESSION_END_REASON_SET',
  SESSION_LAST_SECURITY_CHANGE_SET = 'SESSION_LAST_SECURITY_CHANGE_SET',
}

export enum SessionEndReason {
  SecurityChangeEmail = 'security_change_email',
  SecurityChangeMfa = 'security_change_mfa',
  SessionEnded = 'session_ended',
}

export enum SecurityChangeKind {
  Email = 'email',
  Mfa = 'mfa',
}

export interface SessionEndReasonState {
  reason: SessionEndReason
  at: number
}

export interface LastSecurityChangeState {
  kind: SecurityChangeKind
  at: number
}

export const doSESSION_END_REASON_SET = createAction<SessionEndReasonState | null>(
  SessionActionType.SESSION_END_REASON_SET,
)
export const doSESSION_LAST_SECURITY_CHANGE_SET = createAction<LastSecurityChangeState | null>(
  SessionActionType.SESSION_LAST_SECURITY_CHANGE_SET,
)

export interface SessionState {
  // why this tab was signed out, so the login page can say so; survives the reload
  endReason: SessionEndReasonState | null
  // this tab's own last security save whose proof was incomplete; names the end reason when
  // a terminal refresh lands soon after
  lastSecurityChange: LastSecurityChangeState | null
}

interface RootState {
  session: SessionState
}

export const selectSessionState = () => {
  return useSelector((state: RootState) => state.session)
}

export const sessionActionCreators = {
  doSESSION_END_REASON_SET,
  doSESSION_LAST_SECURITY_CHANGE_SET,
}

export const sessionStateDefaultState: SessionState = {
  endReason: null,
  lastSecurityChange: null,
}

// Persistence is a whitelist, not the whole slice: a field added here later is in memory only
// until it is named below, so nothing can start surviving a reload by accident.
const persistSession = (state: SessionState) => {
  setSessionStorage({session: {endReason: state.endReason, lastSecurityChange: state.lastSecurityChange}})
}

type Action =
  | {type: SessionActionType.SESSION_END_REASON_SET; payload: SessionEndReasonState | null}
  | {type: SessionActionType.SESSION_LAST_SECURITY_CHANGE_SET; payload: LastSecurityChangeState | null}
  | {type: typeof AUTH_SET; payload: unknown}

export const sessionReducer = (
  state: SessionState = {...sessionStateDefaultState},
  {type, payload}: Action,
): SessionState => {
  switch (type) {
    case SessionActionType.SESSION_END_REASON_SET: {
      assertPartialSchema({
        payload,
        schema: v
          .object({
            reason: v
              .valid(...Object.values(SessionEndReason))
              .exist()
              .messages({'any.only': 'endReason.reason must be a known sign-out reason'}),
            at: v.number().exist(),
          })
          .allow(null),
      })

      const nextState = {...state, endReason: payload ?? null}
      persistSession(nextState)
      return nextState
    }

    case SessionActionType.SESSION_LAST_SECURITY_CHANGE_SET: {
      assertPartialSchema({
        payload,
        schema: v
          .object({
            kind: v
              .valid(...Object.values(SecurityChangeKind))
              .exist()
              .messages({'any.only': 'lastSecurityChange.kind must be a known security change'}),
            at: v.number().exist(),
          })
          .allow(null),
      })

      const nextState = {...state, lastSecurityChange: payload ?? null}
      persistSession(nextState)
      return nextState
    }

    // a fresh sign-in answers the message; `lastSecurityChange` is left to expire by age
    case AUTH_SET: {
      if (!state.endReason) return state
      const nextState = {...state, endReason: null}
      persistSession(nextState)
      return nextState
    }

    default:
      return state
  }
}
